'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { useUserStore } from '@/store/user-store'
import { CHECKIN_RULES } from '@/lib/constants'
import { Check, Gift, Star, ChevronRight, Flame } from 'lucide-react'
import { toast } from 'sonner'

export default function CheckinPage() {
  const router = useRouter()
  const { user, lastCheckin, checkinStreak, checkinHistory, checkin, getTodayChecked } = useUserStore()
  const todayChecked = getTodayChecked()
  const [animating, setAnimating] = useState(false)
  const [earnedInfo, setEarnedInfo] = useState<{ points: number; streak: number } | null>(null)

  const today = new Date().toISOString().split('T')[0]
  const historySet = new Set(checkinHistory)

  // 本周签到状态（以本周一为起点）
  const now = new Date()
  const dayOfWeek = now.getDay() === 0 ? 6 : now.getDay() - 1 // 0=周一
  const weekStart = new Date(now)
  weekStart.setDate(now.getDate() - dayOfWeek)

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart)
    d.setDate(weekStart.getDate() + i)
    return {
      date: d.toISOString().split('T')[0],
      day: ['一', '二', '三', '四', '五', '六', '日'][i],
      isToday: d.toISOString().split('T')[0] === today,
      isChecked: historySet.has(d.toISOString().split('T')[0]),
    }
  })

  const handleCheckin = () => {
    if (todayChecked) {
      toast.info('今日已签到')
      return
    }
    const result = checkin()
    if (result.isTodayChecked) {
      toast.info('今日已签到')
      return
    }
    setAnimating(true)
    setEarnedInfo({ points: result.points, streak: result.streak })
    toast.success(`签到成功！获得 ${result.points} 积分`)
    setTimeout(() => setAnimating(false), 1500)
  }

  // 连续签到周期内的天数（7天一循环）
  const cycleDay = ((checkinStreak - 1) % 7) + 1

  if (!user) {
    return (
      <div className="min-h-screen pb-16 bg-gray-50">
        <TopBar />
        <div className="flex flex-col items-center justify-center pt-20">
          <p className="text-gray-400 text-sm">请先登录</p>
          <Link href="/menu" className="mt-3 px-6 py-2 bg-pink-500 text-white text-sm rounded-full">去首页</Link>
        </div>
        <BottomNav />
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar title="每日签到" />

      {/* 签到头部卡片 */}
      <div className="mx-4 mt-3 p-5 bg-gradient-to-br from-pink-500 via-rose-400 to-orange-400 rounded-2xl text-white relative overflow-hidden">
        {/* 装饰圆 */}
        <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full" />
        <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/10 rounded-full" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-5 h-5 text-yellow-300" />
            <span className="text-lg font-bold">每日签到</span>
          </div>
          <p className="text-xs opacity-80 mb-4">坚持签到，积分翻倍领取</p>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold">{checkinStreak}<span className="text-sm font-normal ml-1">天连续签到</span></p>
              <p className="text-xs opacity-70 mt-1">
                {todayChecked ? '✅ 今日已签到' : '今日未签到'}
              </p>
            </div>
            <button
              onClick={handleCheckin}
              disabled={todayChecked}
              className={`px-6 py-3 rounded-full font-bold text-sm transition-all ${
                todayChecked
                  ? 'bg-white/30 text-white/80 cursor-not-allowed'
                  : 'bg-white text-pink-500 active:scale-95 shadow-lg'
              } ${animating ? 'scale-110' : ''}`}
            >
              {todayChecked ? '已签到' : '立即签到'}
            </button>
          </div>
        </div>
      </div>

      {/* 签到动画 */}
      {animating && earnedInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="text-center animate-bounce">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-xl">
              <Star className="w-10 h-10 text-white" />
            </div>
            <p className="text-2xl font-bold text-orange-500 mt-3">+{earnedInfo.points}</p>
            <p className="text-xs text-gray-500">积分已到账</p>
          </div>
        </div>
      )}

      {/* 本周签到日历 */}
      <div className="mx-4 mt-4 bg-white rounded-xl p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-3">本周签到</h3>
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map(day => (
            <div key={day.date} className="flex flex-col items-center gap-1.5">
              <span className="text-xs text-gray-400">{day.day}</span>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                day.isChecked
                  ? 'bg-pink-500 text-white shadow-sm'
                  : day.isToday
                    ? 'bg-pink-100 text-pink-500 border-2 border-pink-300'
                    : 'bg-gray-50 text-gray-400'
              }`}>
                {day.isChecked ? (
                  <Check className="w-4 h-4" />
                ) : (
                  new Date(day.date).getDate()
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 连续签到奖励规则 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-3">连续签到奖励（7天循环）</h3>
        <div className="grid grid-cols-7 gap-1.5">
          {CHECKIN_RULES.map((rule, idx) => {
            const dayNum = idx + 1
            const isActive = cycleDay >= dayNum && checkinStreak > 0
            const isCurrent = cycleDay === dayNum && !todayChecked

            return (
              <div key={dayNum} className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                isActive
                  ? 'bg-pink-50 border border-pink-200'
                  : isCurrent
                    ? 'bg-yellow-50 border-2 border-yellow-300'
                    : 'bg-gray-50 border border-gray-100'
              }`}>
                <div className={`text-xs font-bold ${isActive ? 'text-pink-500' : 'text-gray-500'}`}>
                  第{dayNum}天
                </div>
                <div className={`w-8 h-8 my-1.5 rounded-full flex items-center justify-center ${
                  isActive
                    ? 'bg-gradient-to-br from-pink-500 to-rose-400'
                    : isCurrent
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-400'
                      : 'bg-gray-200'
                }`}>
                  {isActive ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : (
                    <Gift className={`w-4 h-4 ${isCurrent ? 'text-white' : 'text-gray-400'}`} />
                  )}
                </div>
                <div className={`text-xs font-bold ${isActive ? 'text-pink-500' : isCurrent ? 'text-orange-500' : 'text-gray-400'}`}>
                  +{rule.points}
                </div>
              </div>
            )
          })}
        </div>
        <p className="text-[10px] text-gray-400 mt-2 text-center">每7天为一个循环，连续签到奖励更丰厚</p>
      </div>

      {/* 签到记录 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-4 mb-4">
        <h3 className="text-sm font-bold text-gray-800 mb-3">最近签到记录</h3>
        {checkinHistory.length > 0 ? (
          <div className="space-y-2">
            {[...checkinHistory].reverse().slice(0, 10).map((date, idx) => (
              <div key={date} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-pink-500" />
                  </div>
                  <span className="text-xs text-gray-600">{date}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {idx === 0 && checkinStreak > 1 ? `连续${checkinStreak}天` : '已签到'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-3">暂无签到记录</p>
        )}
      </div>

      <BottomNav />
    </div>
  )
}
